// Shared constants for the arcade classics (Asteroids, Invaders, Snake, …).
// Logical units; speeds are per millisecond. Dependency-free.

/* ------------------------------ asteroids ----------------------------- */

export const AST = {
  W: 400, H: 400,
  shipR: 11,
  accel: 0.00040,
  maxV: 0.2,
  friction: 0.0009,
  bulletV: 0.34,
  bulletTtl: 950,
  fireCd: 230,
  lives: 3,
  invulnMs: 2400,
  respawn2P: 1500,
  rockV: 0.035,
  sizes: { 3: 26, 2: 16, 1: 9 } as Record<number, number>,
  points: { 3: 20, 2: 50, 1: 100 } as Record<number, number>,
  playerHitPts: 250,
  target2P: 2500,
  time2P: 120_000,
} as const;

/* ------------------------------ invaders ------------------------------ */

export const INV = {
  W: 360, H: 460,
  cols: 8, rows: 4,
  spanX: 34, spanY: 30,
  startX: 30, startY: 60,
  stepDown: 14,
  baseV: 0.018,
  shipY: 424,
  shipW: 26, shipH: 12,
  shipV: 0.2,
  bulletV: 0.4,
  maxShots: 3, // was 1 ("classic" single-bullet rule) — felt too restrictive
  fireMs: 220, // min gap between shots even with FIRE held down
  bombVBase: 0.11,
  lives: 3,
  respawnMs: 2000,
  rowPoints: [40, 30, 20, 10],
  target2P: 1500,
  bottomLine: 400,
} as const;

/* ------------------------------ directions ----------------------------- */

/** dir: 0 right, 1 down, 2 left, 3 up */
export const DX = [1, 0, -1, 0];
export const DY = [0, 1, 0, -1];

/* -------------------------------- tetris ------------------------------ */

export const TET = {
  COLS: 10,
  ROWS: 20,
  SPAWN_X: 3,
  SPAWN_Y: -1,
  LINES_PER_LEVEL: 10,
  SOFT_MULT: 1, // points per cell of soft drop
  HARD_MULT: 2, // points per cell of hard drop
  LINE_PTS: [0, 100, 300, 500, 800], // by rows cleared, x level
  /** garbage rows sent to the opponent for N rows cleared */
  GARBAGE: [0, 0, 1, 2, 4],
  /** gravity in ms per row at a given (1-based) level */
  gravityMs(level: number): number {
    return Math.max(55, Math.round(800 * Math.pow(0.82, level - 1)));
  },
} as const;

// 7 tetrominoes, 4 rotation states each; cells are [x,y] in a 4x4 box.
// Index 0..6 also serves as the colour id.  I J L O S T Z
export const TET_PIECES: [number, number][][][] = [
  [ // I
    [[0, 1], [1, 1], [2, 1], [3, 1]],
    [[2, 0], [2, 1], [2, 2], [2, 3]],
    [[0, 2], [1, 2], [2, 2], [3, 2]],
    [[1, 0], [1, 1], [1, 2], [1, 3]],
  ],
  [ // J
    [[0, 0], [0, 1], [1, 1], [2, 1]],
    [[1, 0], [2, 0], [1, 1], [1, 2]],
    [[0, 1], [1, 1], [2, 1], [2, 2]],
    [[1, 0], [1, 1], [0, 2], [1, 2]],
  ],
  [ // L
    [[2, 0], [0, 1], [1, 1], [2, 1]],
    [[1, 0], [1, 1], [1, 2], [2, 2]],
    [[0, 1], [1, 1], [2, 1], [0, 2]],
    [[0, 0], [1, 0], [1, 1], [1, 2]],
  ],
  [ // O
    [[1, 0], [2, 0], [1, 1], [2, 1]],
    [[1, 0], [2, 0], [1, 1], [2, 1]],
    [[1, 0], [2, 0], [1, 1], [2, 1]],
    [[1, 0], [2, 0], [1, 1], [2, 1]],
  ],
  [ // S
    [[1, 0], [2, 0], [0, 1], [1, 1]],
    [[1, 0], [1, 1], [2, 1], [2, 2]],
    [[1, 1], [2, 1], [0, 2], [1, 2]],
    [[0, 0], [0, 1], [1, 1], [1, 2]],
  ],
  [ // T
    [[1, 0], [0, 1], [1, 1], [2, 1]],
    [[1, 0], [1, 1], [2, 1], [1, 2]],
    [[0, 1], [1, 1], [2, 1], [1, 2]],
    [[1, 0], [0, 1], [1, 1], [1, 2]],
  ],
  [ // Z
    [[0, 0], [1, 0], [1, 1], [2, 1]],
    [[2, 0], [1, 1], [2, 1], [1, 2]],
    [[0, 1], [1, 1], [1, 2], [2, 2]],
    [[1, 0], [0, 1], [1, 1], [0, 2]],
  ],
];

/* --------------------------- color block blaster ------------------------ */

export const BLK = {
  COLS: 8,
  ROWS: 8,
  TRAY_SIZE: 3,
  CELL_PTS: 1, // points per cell placed
  LINE_PTS: 10, // points per cell in a cleared line
  COMBO_MULT: 0.5, // extra multiplier per additional simultaneous line cleared
} as const;

// Every shape a piece can appear in — no rotate control (matching the
// original), so each visual orientation is its own catalog entry. Offsets
// are [dx, dy] from the piece's own top-left cell.
export const BLK_PIECES: [number, number][][] = [
  [[0, 0]],
  [[0, 0], [1, 0]],
  [[0, 0], [0, 1]],
  [[0, 0], [1, 0], [2, 0]],
  [[0, 0], [0, 1], [0, 2]],
  [[0, 0], [1, 0], [0, 1]],
  [[0, 0], [1, 0], [1, 1]],
  [[0, 1], [1, 1], [1, 0]],
  [[0, 0], [0, 1], [1, 1]],
  [[0, 0], [1, 0], [0, 1], [1, 1]],
  [[0, 0], [1, 0], [2, 0], [3, 0]],
  [[0, 0], [0, 1], [0, 2], [0, 3]],
  [[0, 0], [0, 1], [0, 2], [1, 2]],
  [[0, 0], [1, 0], [2, 0], [0, 1]],
  [[0, 0], [1, 0], [1, 1], [1, 2]],
  [[2, 0], [0, 1], [1, 1], [2, 1]],
  [[1, 0], [1, 1], [1, 2], [0, 2]],
  [[0, 0], [0, 1], [1, 1], [2, 1]],
  [[0, 0], [1, 0], [0, 1], [0, 2]],
  [[0, 0], [1, 0], [2, 0], [2, 1]],
  [[0, 0], [1, 0], [2, 0], [1, 1]],
  [[0, 0], [0, 1], [0, 2], [1, 1]],
  [[1, 0], [0, 1], [1, 1], [2, 1]],
  [[1, 0], [1, 1], [1, 2], [0, 1]],
  [[1, 0], [2, 0], [0, 1], [1, 1]],
  [[0, 0], [0, 1], [1, 1], [1, 2]],
  [[0, 0], [1, 0], [1, 1], [2, 1]],
  [[1, 0], [0, 1], [1, 1], [0, 2]],
  [[1, 0], [0, 1], [1, 1], [2, 1], [1, 2]],
  [[0, 0], [1, 0], [0, 1], [1, 1], [0, 2]],
  [[0, 0], [0, 1], [0, 2], [0, 3], [1, 3]],
  [[1, 0], [0, 1], [1, 1], [1, 2], [1, 3]],
  [[0, 0], [1, 0], [2, 0], [3, 0], [4, 0]],
  [[0, 0], [0, 1], [0, 2], [0, 3], [0, 4]],
];

/* ------------------------------- mini golf ------------------------------ */

export const GOLF = {
  W: 340,
  H: 600,
  BORDER: 14,
  BALL_R: 7,
  CUP_R: 11,
  FRICTION: 0.0022, // px/ms^2 deceleration
  // was 0.55 (a full-pull shot only traveled ~69px against a hole roughly
  // 480-530px away — every hole needed 7-8 straight-line strokes minimum,
  // which felt weak/floaty). Bumped to 1.25 (~355px/pull), then dialed back
  // 20% per feedback that it was now too fast/far — 1.0 travels ~227px.
  MAX_POWER: 1.0, // px/ms at full pull
  STOP_SPEED: 0.02,
  RESTITUTION: 0.72,
  MAX_STROKES: 8, // hole auto-sinks a ball that can't finish, so a match can't stall forever
} as const;

// Each hole: tee (start), cup (target), and a handful of wall rectangles —
// obstacles genuinely vary hole to hole, from a wide-open first green to a
// walled corridor by the last.
export const GOLF_HOLES: { tee: [number, number]; cup: [number, number]; walls: [number, number, number, number][] }[] = [
  { tee: [170, 540], cup: [170, 60], walls: [] },
  { tee: [170, 540], cup: [170, 60], walls: [[120, 300, 100, 20]] },
  {
    tee: [170, 540], cup: [170, 60],
    walls: [[14, 380, 130, 20], [196, 380, 130, 20], [14, 220, 210, 20], [280, 220, 46, 20]],
  },
  {
    tee: [60, 540], cup: [280, 60],
    walls: [[100, 40, 20, 260], [220, 300, 20, 260], [0, 300, 100, 20], [240, 300, 100, 20]],
  },
];

/* -------------------------------- snake ------------------------------- */

export const SNK = {
  /** default (portrait) board — 1v1 and the web client; a phone playing solo
   *  asks for a grid shaped like its own screen (see GRID below) */
  W: 18,
  H: 30,
  /** bounds for a phone's requested solo grid ("gridHint") */
  GRID: { MIN_W: 12, MAX_W: 26, MIN_H: 16, MAX_H: 48 },
  /** turns buffered ahead of the snake — enough for a stair-step burst
   *  (up-left-up-left…) tapped faster than the snake moves */
  QUEUE: 6,
  START_LEN: 4,
  START_MS: 160,
  MIN_MS: 70,
  /** every this many apples eaten, the snake speeds up (solo) */
  SPEEDUP_EVERY: 4,
  SPEEDUP_MS: 8,
  APPLE_PTS: 10,
  FOOD_SOLO: 1,
  FOOD_2P: 3,
  KILL_PTS: 50, // 2P: causing the rival to crash into you
  WIN_LEN_2P: 30, // 2P: first snake to this length wins outright
} as const;

/* ------------------------- strait of hormuz ------------------------- */

export const HRZ = {
  W: 400,
  H: 580,
  COAST_N: 84,   // water is y in (COAST_N, COAST_S)
  COAST_S: 500,
  SHIP: { x: 200, y: 548 }, // your warship, fixed
  // AIM_SPEED is only still read by the (feature-frozen) web client's own
  // client-side prediction — the native app now sends a direct target
  // `angle` instead of an `ax` rate, so the server no longer integrates
  // this for that path. Don't remove it: src/client/games/hormuz.ts
  // depends on it and that file isn't touched from here.
  AIM_SPEED: 0.0016,        // radians/ms of turret swing at full stick, left/right only
  AIM_MAX: 1.4,             // radians either side of straight-up (~80°)
  // a small random deviation applied to each shot's actual flight angle —
  // the turret itself aims exactly where you tap (no wobble), this is just
  // enough imprecision that a shot can clip a target standing right next
  // to the one you aimed at
  SHOT_SPREAD: 0.035,       // radians (~2deg) either side of aimAngle
  RELOAD_MS: 950,
  AMMO_MAX: 7,
  INT_V: 0.62,              // interceptor speed
  BLAST_R: 34,              // interceptor detonation radius
  HIT_R: 16,                // a flying shot this close to a threat detonates on it
  MINE_R: 9,                // a ship this close to an armed mine is hit
  MINE_FORGIVE: 0.9,        // the trigger distance is scaled by this (10% tighter than MINE_R + ship size)
  MINE_ARM_MS: 1430,        // harmless this long after it appears (was 1300; +10%)
  SITE_CHARGE_MS: 1900,
  SITE_COOLDOWN: [4200, 8200],
  SITE_CAP: 5,
  DRONE_V: 0.10,
  MISSILE_V: 0.22,
  SHIP_V: [0.026, 0.038],   // tanker, cargo
  SHIP_HP: [3, 2],
  HULL: 6,
  RAMP_MS: 230_000,
  SPAWN_MS: [4600, 1500],   // event interval: start, end (by intensity)
  SHIP_MS: [3400, 2100],
  BURST: [0.06, 0.42],      // chance of a simultaneous 2-3 event burst
  PTS: { survive: 8, escort: 250, air: 15, site: 35, mine: 15 },
} as const;

/* -------------------------------- worms ------------------------------- */

export const WRM = {
  W: 520,
  H: 320,
  COLS: 120,
  WATER: 306,          // y at/below which a worm drowns
  GRAV: 0.00024,       // px / ms^2
  BASE_V: 0.64,        // muzzle speed at power 1.0 (small bomb)
  SIZE_V: [1, 0.78, 0.6],    // speed multiplier — bigger bomb flies shorter
  SIZE_R: [16, 25, 36],      // crater radius
  SIZE_DMG: [28, 46, 66],    // max damage at the centre
  WIND_MAX: 0.00011,
  TURN_MS: 22000,
  THINK_MS: 1400,
  WORM_HP: 100,
  FALL_DMG: 0.4,       // hp per px fallen past 24px
  BLOCK_HP: 45,
  MUZZLE: 9,           // barrel length from worm centre
} as const;
export const WRM_SIZES = ["S", "M", "L"];
